import { useCallback, useEffect } from 'react'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { useImports } from '@/api/endpoints/videoCutter'
import { ProjectEditor } from './components/ProjectEditor'
import { ProjectsStage } from './components/ProjectsStage'
import { useProjects } from './hooks/useProjects'
import type { ProjectVideo, ToolProject } from './model'
import { migrateLegacyImports, projectFromVideo } from './projectStore'

/**
 * Clip creator.
 *
 * Two screens, discriminated by whether a project is open: the project
 * list (which is also where new ones are imported) and the editor. The
 * project itself — name, videos, clips, order — lives in localStorage
 * (see ``projectStore``); the media lives on the server.
 *
 * Which project is open lives in ``?project=`` rather than component
 * state: a reload in the middle of an edit has to come back to the
 * editor, and back/forward should walk between list and project.
 */
export function ClipCreatorPage() {
  const { projects, ready, addProject, updateProject, removeProject } = useProjects()
  const navigate = useNavigate()
  const search = useSearch({ from: '/_app/tools/clip-creator' })
  const openId = search.project ?? null
  const imports = useImports()

  // Pushed, not replaced: opening a project is a step the back button
  // should undo, the same way it does in any list → detail screen.
  const setOpenId = useCallback(
    (projectId: string | null) => {
      void navigate({
        to: '/tools/clip-creator',
        search: (prev) => {
          const next = { ...prev }
          if (projectId) next.project = projectId
          else delete next.project
          return next
        },
      })
    },
    [navigate],
  )

  // One-shot adoption of the pre-project world: every cached video becomes
  // a project carrying whatever clips were saved for it. Waits for both
  // the imports list and the (async) localStorage read so nothing is
  // duplicated or dropped.
  useEffect(() => {
    if (!ready || !imports.data) return
    const adopted = migrateLegacyImports(imports.data)
    if (!adopted) return
    for (const project of adopted) addProject(project)
  }, [ready, imports.data, addProject])

  const openProject = projects.find((p) => p.id === openId) ?? null

  // Stable across renders so the editor's callbacks (and the drag
  // listeners built on them) don't churn on every state update.
  const changeOpenProject = useCallback(
    (updater: (project: ToolProject) => ToolProject) => {
      if (openId) updateProject(openId, updater)
    },
    [openId, updateProject],
  )

  const createProject = (video: ProjectVideo) => {
    const project = projectFromVideo(video)
    addProject(project)
    setOpenId(project.id)
  }

  const deleteProject = (project: ToolProject) => {
    if (!window.confirm(`Excluir o projeto "${project.name}"? Os vídeos continuam no servidor.`)) {
      return
    }
    removeProject(project.id)
    if (openId === project.id) setOpenId(null)
  }

  if (!openProject) {
    // Reload with ``?project=`` set: hold the screen until localStorage is
    // read, or every refresh flashes the project list before the editor.
    if (openId && !ready) return null
    return (
      <ProjectsStage
        projects={projects}
        ready={ready}
        onOpen={setOpenId}
        onDelete={deleteProject}
        onCreate={createProject}
      />
    )
  }

  return (
    <ProjectEditor
      // Remount on project switch: the editor holds per-project transient
      // state (active video, arming, zoom) that must not leak across.
      key={openProject.id}
      project={openProject}
      onChange={changeOpenProject}
      onClose={() => {
        setOpenId(null)
      }}
    />
  )
}
